if (!customElements.get('taili-overlay-header')) {
  class TailiOverlayHeader extends HTMLElement {
    constructor() {
      super();
      this.desktopQuery = window.matchMedia('(min-width: 1025px)');
      this.menuItems = [];
      this.activeMenuItem = null;
      this.mobileToggle = null;
      this.mobileDrawer = null;
      this.mobileCloseButtons = [];
      this.menuShade = null;
      this.closeTimer = null;
      this.scrollFrame = null;
      this.lastFocusedElement = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
    }

    connectedCallback() {
      this.menuItems = Array.from(this.querySelectorAll('[data-menu-item]'));
      this.mobileToggle = this.querySelector('[data-mobile-toggle]');
      this.mobileDrawer = this.querySelector('[data-mobile-drawer]');
      this.mobileCloseButtons = Array.from(this.querySelectorAll('[data-mobile-close]'));
      this.menuShade = this.querySelector('[data-menu-shade]');

      this.bindDesktopMenus();
      this.bindMobileDrawer();

      this.menuShade?.addEventListener('click', () => this.closeDesktopMenus());
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.desktopQuery.addEventListener('change', this.handleViewportChange);

      this.updateScrolledState();
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('scroll', this.handleScroll);
      this.desktopQuery.removeEventListener('change', this.handleViewportChange);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.unlockBody();
    }

    bindDesktopMenus() {
      this.menuItems.forEach((item) => {
        const trigger = item.querySelector('[data-menu-trigger]');
        const menu = item.querySelector('[data-super-menu]');
        if (!trigger || !menu) return;

        item.addEventListener('pointerenter', () => {
          if (!this.desktopQuery.matches) return;
          clearTimeout(this.closeTimer);
          this.openDesktopMenu(item);
        });

        item.addEventListener('pointerleave', () => {
          if (!this.desktopQuery.matches) return;
          this.closeTimer = window.setTimeout(() => this.closeDesktopMenus(), 120);
        });

        item.addEventListener('focusin', () => {
          if (this.desktopQuery.matches) this.openDesktopMenu(item);
        });

        item.addEventListener('focusout', (event) => {
          if (!this.desktopQuery.matches || item.contains(event.relatedTarget)) return;
          this.closeTimer = window.setTimeout(() => {
            if (!item.contains(document.activeElement)) this.closeDesktopMenus();
          }, 0);
        });

        trigger.addEventListener('click', (event) => {
          if (!this.desktopQuery.matches) return;
          if (this.activeMenuItem !== item) {
            event.preventDefault();
            this.openDesktopMenu(item);
          }
        });

        trigger.addEventListener('keydown', (event) => {
          if (!this.desktopQuery.matches) return;
          if (event.key === 'ArrowDown' || event.key === ' ') {
            event.preventDefault();
            this.openDesktopMenu(item);
            menu.querySelector('a')?.focus();
          }
        });
      });
    }

    bindMobileDrawer() {
      this.mobileToggle?.addEventListener('click', () => this.openMobileDrawer());
      this.mobileCloseButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeMobileDrawer());
      });

      this.mobileDrawer?.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.closeMobileDrawer(false);
      });
    }

    openDesktopMenu(item) {
      if (!this.desktopQuery.matches) return;
      clearTimeout(this.closeTimer);

      this.menuItems.forEach((candidate) => {
        const isTarget = candidate === item;
        const trigger = candidate.querySelector('[data-menu-trigger]');
        const menu = candidate.querySelector('[data-super-menu]');
        candidate.classList.toggle('is-open', isTarget);
        trigger?.setAttribute('aria-expanded', String(isTarget));
        if (menu) menu.hidden = !isTarget;
      });

      this.activeMenuItem = item;
      this.classList.add('is-menu-open');
      if (this.menuShade) this.menuShade.hidden = false;
    }

    closeDesktopMenus() {
      clearTimeout(this.closeTimer);
      this.menuItems.forEach((item) => {
        item.classList.remove('is-open');
        item.querySelector('[data-menu-trigger]')?.setAttribute('aria-expanded', 'false');
        const menu = item.querySelector('[data-super-menu]');
        if (menu) menu.hidden = true;
      });
      this.activeMenuItem = null;
      this.classList.remove('is-menu-open');
      if (this.menuShade) this.menuShade.hidden = true;
    }

    openMobileDrawer() {
      if (!this.mobileDrawer || !this.mobileToggle) return;
      this.closeDesktopMenus();
      this.lastFocusedElement = document.activeElement;
      this.mobileDrawer.hidden = false;
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-mobile-open');
      this.lockBody();

      requestAnimationFrame(() => {
        this.mobileDrawer.querySelector('[data-mobile-close]')?.focus();
      });
    }

    closeMobileDrawer(restoreFocus = true) {
      if (!this.mobileDrawer || !this.mobileToggle) return;
      this.classList.remove('is-mobile-open');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      this.unlockBody();

      window.setTimeout(() => {
        if (this.mobileDrawer) this.mobileDrawer.hidden = true;
        if (restoreFocus) this.lastFocusedElement?.focus?.();
      }, this.prefersReducedMotion() ? 0 : 260);
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.closeDesktopMenus();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.classList.contains('is-mobile-open')) {
        this.closeMobileDrawer();
        return;
      }

      if (this.activeMenuItem) {
        const trigger = this.activeMenuItem.querySelector('[data-menu-trigger]');
        this.closeDesktopMenus();
        trigger?.focus();
      }
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.updateScrolledState();
        this.scrollFrame = null;
      });
    }

    updateScrolledState() {
      const stickyEnabled = this.dataset.sticky === 'true';
      this.classList.toggle('is-scrolled', stickyEnabled && window.scrollY > 24);
    }

    handleViewportChange(event) {
      if (event.matches) {
        if (this.classList.contains('is-mobile-open')) this.closeMobileDrawer(false);
      } else {
        this.closeDesktopMenus();
      }
    }

    lockBody() {
      document.body.classList.add('overflow-hidden');
    }

    unlockBody() {
      document.body.classList.remove('overflow-hidden');
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('taili-overlay-header', TailiOverlayHeader);
}
